import { EditableCallbackParams, ICellEditorParams, ICellRendererParams, IRowNode, ValueFormatterParams } from "ag-grid-enterprise";
import { Modal, Title } from "components";
import { AgGrid, AgGridRef, ISelectRowOnClickParams, ZColDef } from "components/AgGrid/AgGrid";
import OverflowLabel from "components/OverflowLabel";
import GlobalFilter from "components/Table/GlobalFilter";
import { ZButton } from "components/ZButton";
import { ZCounter } from "components/ZCounter";
import { ZIcon } from "components/ZIcon";
import { ZTooltip } from "components/ZTooltip";
import { statesToString } from "constants/constants";
import { groupBy, hasDuplicates } from "helpers/Functions";
import { ZIconType } from "helpers/utility/iconMap";
import { LinkRequest } from "models/daoModels/managedStock/MergeRequest";
import { ManagedStockConflict, ManagedStockPdeApnConflict } from "models/dbModels/conflicts/ManagedStockConflict";
import { ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { Product } from "models/dbModels/products/Product";
import { ManagedStockSearchFilterQueryLoadDetails } from "models/filters/managedStocks/ManagedStockSearchFilterQuery";
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row, Tab, Tabs } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getProducts } from "redux/slices/Products";
import { getStockConflicts, getStocks, linkToProduct } from "redux/slices/Stocks";
import { getSuppliers } from "redux/slices/Suppliers";

type ProductConflictInfo = {
    id: number,
    name: string;
}

type LinkConflictRowData = {
    managedStockId: number;
    managedStockName: string;
    conflictingProducts: ProductConflictInfo[]; // List of conflicts for a given managed stock (could be more than 1 for a given managed stock)
    conflict: ProductConflictInfo | undefined; // The actual selected conflict to link
}

type DeleteConflictRowData = ManagedStockPdeApnConflict;

type StockCompare = {
    stockA: ManagedStockView;
    stockB: Product;
}

export function ManagedStockConflicts() {
    const linkGridRef = useRef<AgGridRef>(null);
    const deleteGridRef = useRef<AgGridRef>(null);
    const dispatch = useAppDispatch();
    const [showLinkConfirmPrompt, setShowLinkConfirmPrompt] = useState<boolean>(false);
    const [showDeleteConfirmPrompt, setShowDeleteConfirmPrompt] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [linkConflictRowData, setLinkConflictRowData] = useState<LinkConflictRowData[]>([]);
    const [deleteConflictRowData, setDeleteConflictRowData] = useState<DeleteConflictRowData[]>([]);
    const [selectedStocksHaveError, setSelectedStocksHaveError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [stockCompare, setStockCompare] = useState<StockCompare | undefined>(undefined);
    const [stockCompareModalOpen, setStockCompareModalOpen] = useState<boolean>(false);
    const suppliers = useAppSelector((state) => state.suppliers);

    const defaultDeleteConflictColumnDefs: ZColDef<DeleteConflictRowData>[] = [
        {
            headerName: "Managed Stock",
            field: "stockName1",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            sortable: true
        },
        {
            headerName: "System Match",
            field: "stockName2",
            flex: 1,
            minWidth: 400,
            sortable: true,
        },
        {
            headerName: "Message",
            field: "message",
            flex: 1,
            minWidth: 400,
            sortable: true,
        }
    ];

    const conflictColumnDefs = useMemo<ZColDef<LinkConflictRowData>[]>(() => [
        {
            headerName: "Managed Stock",
            field: "managedStockName",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            sortable: true,
        },
        {
            headerName: "System Match",
            field: "conflict",
            flex: 1,
            minWidth: 400,
            sortable: true,
            selectRowOnClick: (params: ISelectRowOnClickParams<LinkConflictRowData>) => {
                return params.data.conflictingProducts.length < 2;
            },
            editable: (params: EditableCallbackParams<LinkConflictRowData>) => {
                return params.data != null && params.data.conflictingProducts.length > 1;
            },
            valueFormatter: (params: ValueFormatterParams<LinkConflictRowData>) => {
                return params.data?.conflict?.name ?? "Multiple conflicts detected, please select a system stock to link";
            },
            cellRendererParams: (params: ICellRendererParams<LinkConflictRowData>) => {
                setSelectedStocksHaveError(false);
                const conflict = params.data?.conflict;
                if (conflict == null) {
                    return {
                        decorator: <ZIcon variant="warning" icon={ZIconType.Alert} size="14px" iconClassName={`me-1`} />,
                        decoratorPosition: "start",
                    }
                } else {
                    if (hasDuplicates(linkConflictRowData.filter(r => r.conflict?.id === conflict.id).map(r => r.conflict?.id))) {
                        const dupeConflicts = linkConflictRowData.filter(r => r.conflict?.id === conflict.id && r.managedStockId !== params.data?.managedStockId);
                        setSelectedStocksHaveError(true);
                        return {
                            decorator: <ZTooltip text={`This system stock is already being linked to ${dupeConflicts.map(c => c.managedStockName).join(", ")}`}><ZIcon variant="danger" icon={ZIconType.Error} size="14px" iconClassName={`me-1`} /></ZTooltip>,
                            decoratorPosition: "start",
                        }
                    }
                }
            },
            cellEditorSelector: (params: ICellEditorParams<LinkConflictRowData>) => {
                if (params.data.conflictingProducts.length > 1) {
                    return { 
                        component: 'agRichSelectCellEditor', 
                        params: {
                            values: [undefined, ...params.data.conflictingProducts], formatValue: (value: ProductConflictInfo) => value?.name ?? ""
                        }
                    };
                } else {
                    return undefined;
                }
            }
        },
        {
            headerName: "",
            pinned: "right",
            cellRendererSelector: (params: ICellRendererParams<LinkConflictRowData>) => {
                return {
                    component: 'baseCellRenderer',
                    params: {
                        decorator: 
                            <div className="w-100 h-100 d-inline-flex justify-content-center gap-1">
                                <ZTooltip text={params.data?.conflict == null ? "You must select a product match to compare" : "Compare"}>
                                    <ZButton className="custom-btn-icon" disabled={params.data?.conflict == null} icon={ZIconType.Compare} onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (params.data) {
                                            dispatch(getProducts({ids: params.data.conflict?.id != null ? [params.data.conflict?.id] : undefined, loadApns: true, loadPdes: true})).then((res1) => {
                                                dispatch(getStocks({ids: [params.data!.managedStockId], loadDetails: [ManagedStockSearchFilterQueryLoadDetails.Apns, ManagedStockSearchFilterQueryLoadDetails.Pdes]})).then((res2) => {
                                                    const stocks = res2.data;
                                                    const stockA = stocks.find(s => s.id === params.data!.managedStockId)!;
                                                    const stockB = res1.data.find(p => p.id === params.data!.conflict!.id)! as Product;
                                                    // We need to add a flag to the apns and pdes to indicate if they are in conflict for styling the cells in the grid
                                                    // Setting the flags for stockA
                                                    const updatedStockA = {
                                                        ...stockA,
                                                        apns: stockA.apns.map(a => ({
                                                            ...a,
                                                            conflict: stockB.apns.find(b => b.apn === a.apn) != null
                                                        })),
                                                        pdes: stockA.pdes.map(p => ({
                                                            ...p,
                                                            conflict: stockB.pdes.find(b => b.pde === p.pde) != null
                                                        }))
                                                    };

                                                    const updatedStockB = {
                                                        ...stockB,
                                                        apns: stockB.apns.map(a => ({
                                                            ...a,
                                                            conflict: stockA.apns.find(b => b.apn === a.apn) != null
                                                        })),
                                                        pdes: stockB.pdes.map(p => ({
                                                            ...p,
                                                            conflict: stockA.pdes.find(b => b.pde === p.pde) != null
                                                        }))
                                                    };

                                                    // Sort the apns and pdes by conflict status so conflicts are at the top
                                                    updatedStockA.apns.sort((a, b) => a.conflict === b.conflict ? 0 : a.conflict ? -1 : 1);
                                                    updatedStockA.pdes.sort((a, b) => a.conflict === b.conflict ? 0 : a.conflict ? -1 : 1);
                                                    updatedStockB.apns.sort((a, b) => a.conflict === b.conflict ? 0 : a.conflict ? -1 : 1);
                                                    updatedStockB.pdes.sort((a, b) => a.conflict === b.conflict ? 0 : a.conflict ? -1 : 1);

                                                    console.log(updatedStockA, updatedStockB);
                                                    setStockCompare({
                                                        stockA: updatedStockA,
                                                        stockB: updatedStockB,
                                                    });
                                                    setStockCompareModalOpen(true);
                                                });
                                            });
                                            
                                        }
                                    }}/>
                                </ZTooltip>
                                <ZTooltip text={params.data?.conflict == null ? "You must select a product match to link" : "Link"}>
                                    <ZButton className="custom-btn-icon" disabled={params.data?.conflict == null} icon={ZIconType.Link} onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowLinkConfirmPrompt(true);
                                    }}/>
                                </ZTooltip>
                            </div>,
                        decoratorPosition: "start"
                    }
                }
            },
        }
    ], [linkConflictRowData]);

    const [rowsToLink, setRowsToLink] = useState<LinkConflictRowData[]>([]);
    const [rowsToDelete, setRowsToDelete] = useState<DeleteConflictRowData[]>([]);


    useEffect(() => {
        dispatch(getSuppliers());
        (async () => {
            setLoading(true);
            const linkConflictPromise = dispatch(getStockConflicts()).then((res) => {
                // Group data by managedStockId since we might have multiple managedStockId => productId conflicts
                setLinkConflictRowData(stockConflictToLinkConflictRowData(res.data));
            });
    
   
            await Promise.all([linkConflictPromise]).then(() => {
                setLoading(false);
            });
        })();
    }, []);

    function stockConflictToLinkConflictRowData(conflicts: ManagedStockConflict[]): LinkConflictRowData[] {
        const groupedData = groupBy(conflicts, (r) => r.stockId1);
        let newData: LinkConflictRowData[] = [];
        groupedData.forEach((v, k, m) => {
            const conflictingProducts: ProductConflictInfo[] = v.map((x, ix) => {
                return {
                    id: x.stockId2,
                    name: x.stockName2
                }
            });
            const conflictData: LinkConflictRowData = {
                managedStockId: k,
                managedStockName: v[0].stockName1,
                conflictingProducts: conflictingProducts,
                conflict: conflictingProducts.length > 1 ? undefined : { id: conflictingProducts[0].id, name: conflictingProducts[0].name } // If we have multiple conflicts, set the value to be undefined (empty), otherwise grab v[0]
            }
            newData = [...newData, conflictData];
        });

        return newData;
    }

    function handleLinkConfirm() {
        setShowLinkConfirmPrompt(false);
        setLoading(true);
        // Send rowsToLink to link endpoint
        dispatch(linkToProduct([...rowsToLink.filter(r => r.conflict != null).map<LinkRequest>(r => {
            return {
                productId: r.conflict!.id,
                managedId: r.managedStockId
            }
        })])).then((res) => {
            dispatch(getStockConflicts()).then((res) => {
                const data = res.data;
                setLinkConflictRowData(stockConflictToLinkConflictRowData(data));
                setLoading(false);
            });
        })
    }

    function handleSingleLink(request: LinkRequest) {
        setShowLinkConfirmPrompt(false);
        setLoading(true);
        dispatch(linkToProduct([request])).then((res) => {
            dispatch(getStockConflicts()).then((res) => {
                const data = res.data;
                setLinkConflictRowData(stockConflictToLinkConflictRowData(data));
                setLoading(false);
            });
        })
    }
    
    function handleDeleteConfirm() {
        setShowDeleteConfirmPrompt(false);
        setLoading(true);
        // TODO: Need to implement this but first the backend needs to be changed to accept a list of ids or something
        setLoading(false);
    }

    function CompareCard(stock: ManagedStockView | Product, isProduct: boolean = false) {
        if (stock == null) return <></>;

        return (
            <Card>
                <Card.Header className="bg-primary text-white p-1">
                    <div className="d-inline-flex gap-2 align-items-center ms-1 w-100 pe-1">
                        {isProduct && <ZTooltip text="This is a system stock"><ZIcon icon={ZIconType.System} size="18px" /></ZTooltip>}
                        <OverflowLabel labelClassName="h4 my-2" tooltipStyle={{zIndex: 99999}} text={stock?.name ?? ""} isVisible/>
                    </div>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col xs={12}>
                            <h5>PDE(s):</h5>
                            <AgGrid
                                heightPx={250}
                                rowData={stock.pdes}
                                columnDefs={[
                                    {
                                        headerName: "PDE",
                                        field: "pde",
                                        cellRendererParams: (params: ICellRendererParams) => {
                                            return {
                                                decorator: <ZTooltip enabled={params.data.isSystem} placement="top" text="This is a system PDE"><ZIcon icon={ZIconType.System} iconClassName={`me-1 ${params.data.isSystem === true || isProduct ? "" : "invisible"}`} /></ZTooltip>,
                                                decoratorPosition: "start"
                                            }
                                        },
                                        cellStyle: (params: ValueFormatterParams) => {
                                            return {
                                                color: params.data.conflict === true ? "red" : "black",
                                                fontWeight: params.data.conflict === true ? "bold" : "normal"
                                            }
                                        }
                                    },
                                    {
                                        headerName: "Supplier",
                                        field: isProduct ? "supplierCode" : "supplierId",
                                        valueFormatter: (params: ValueFormatterParams) => {
                                            return suppliers.table.find(s => isProduct ? s.systemCode === params.value : s.id === params.value)?.name ?? "";
                                        },
                                        cellStyle: (params: ValueFormatterParams) => {
                                            return {
                                                color: params.data.conflict === true ? "red" : "black",
                                                fontWeight: params.data.conflict === true ? "bold" : "normal"
                                            }
                                        }
                                    },
                                    // {
                                    //     headerName: "State",
                                    //     field: "states",
                                    //     valueFormatter: (params: ValueFormatterParams) => {
                                    //         return statesToString(params.value);
                                    //     },
                                    //     cellStyle: (params: ValueFormatterParams) => {
                                    //         return {
                                    //             color: params.data.conflict === true ? "red" : "black",
                                    //             fontWeight: params.data.conflict === true ? "bold" : "normal"
                                    //         }
                                    //     }
                                    // }
                                ]}
                                loading={false}
                            />
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col xs={12}>
                            <h5>APN(s):</h5>
                            <AgGrid
                                heightPx={200}
                                rowData={stock.apns}
                                columnDefs={[
                                    {
                                        headerName: "APN",
                                        field: "apn",
                                        flex: 1,
                                        resizable: false,
                                        cellStyle: (params: ValueFormatterParams) => {
                                            return {
                                                color: params.data.conflict === true ? "red" : "black",
                                                fontWeight: params.data.conflict === true ? "bold" : "normal"
                                            }
                                        }
                                    }
                                ]}
                                loading={false}
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div className="stocks-page">
            <Modal 
                title="Stock Comparison"
                show={stockCompareModalOpen}
                buttonCloseText="Close"
                hideMainButton
                size="xl"
                handleClose={() => setStockCompareModalOpen(false)}
                description={
                    <>
                    {/* <Row>
                        <Col xs={2}>
                            <p className="fw-bold">Name</p>
                        </Col>
                        <Col xs={5}>
                            <OverflowLabel text={stockCompare?.stockA?.name ?? ""} isVisible={true}/>
                        </Col>
                        <Col xs={5} className="d-flex">
                        <OverflowLabel labelClassName="h3" tooltipStyle={{zIndex: 99999}} text={stockCompare?.stockB?.name ?? ""} isVisible/>
                        </Col>
                    </Row> */}
                    <Row>
                        <Col xs={4}>
                            {CompareCard(stockCompare?.stockA!)}
                        </Col>
                        <Col xs={4}>
                            {CompareCard(stockCompare?.stockB!, true)}
                        </Col>
                        <Col xs={4}>
                            {CompareCard(stockCompare?.stockB!, true)}
                        </Col>
                        {/* <AgGrid
                            containerStyle={{
                                marginBottom: "1rem"
                            }}
                            heightPx={250}
                            rowData={mapCompareToAPNRowData(stockCompare!)}
                            columnDefs={[
                                {
                                    headerName: stockCompare?.stockA?.name ?? "",
                                    children: [
                                        {
                                            headerName: "APN",
                                            field: "apnA",
                                        },
                                    ]
                                },
                                {
                                    headerName: stockCompare?.stockB?.name ?? "",
                                    children: [
                                        {
                                            headerName: "APN",
                                            field: "apnB",
                                        },
                                    ]
                                }
                            ]}
                        />
                        <AgGrid
                            heightPx={250}
                            rowData={mapCompareToPDERowData(stockCompare!)}
                            columnDefs={[
                                {
                                    headerName: stockCompare?.stockA?.name ?? "",
                                    children: [
                                        {
                                            headerName: "PDE",
                                            field: "pdeA",
                                        },
                                        {
                                            headerName: "State",
                                            field: "stateA",
                                        },
                                        {
                                            headerName: "Supplier",
                                            field: "supplierA",
                                        }
                                    ]
                                },
                                {
                                    headerName: stockCompare?.stockB?.name ?? "",
                                    children: [
                                        {
                                            headerName: "PDE",
                                            field: "pdeB",
                                        },
                                        {
                                            headerName: "State",
                                            field: "stateB",
                                        },
                                        {
                                            headerName: "Supplier",
                                            field: "supplierB",
                                        }
                                    ]
                                }
                            ]}
                            loading={false} 
                        /> */}
                    </Row>
                    </>
                }
            />
            <Modal
                title="Confirm - Link stocks"
                show={showLinkConfirmPrompt}
                size="sm"
                buttonType="primary"
                buttonText={"Continue"}
                handleClose={() => setShowLinkConfirmPrompt(false)}
                handleSubmit={handleLinkConfirm}
                description={
                    <>
                        <p>You are about to link {rowsToLink.length} stocks.</p>
                        <p className="mb-0">Are you sure you want to continue?</p>
                    </>
                }
            />
            
            <Modal
                title="Confirm - Fix stocks"
                show={showDeleteConfirmPrompt}
                buttonType="primary"
                buttonText={"Fix"}
                handleClose={() => setShowDeleteConfirmPrompt(false)}
                handleSubmit={handleDeleteConfirm}
                description={
                    <>
                        <p>You are about to fix {rowsToDelete.length} stocks.</p>
                        <p className="mb-0">Are you sure you want to continue?</p>
                    </>
                }
            />
            <Title pageTitle={"Stock Conflicts"} />
            <Row>
                <Col xs={12} style={{ padding: 0 }}>
                    <Card>
                        <Card.Body>
                        <Tabs
                            className="ag-grid-tabs-container"
                            defaultActiveKey="link"
                        >
                            <Tab eventKey={"link"} title={<span className="d-inline-flex align-items-center">Link <ZCounter className="ms-1" counter={linkConflictRowData.length} /></span>} className="pt-2">
                                <Row>
                                    <Col xs={12}>
                                        <GlobalFilter onSearchClick={(s) => setSearchText(s ?? "")} placeholder={`Search ${linkConflictRowData.length} conflicts`} preGlobalFilteredRows={[]} globalFilter={""} setGlobalFilter={() => {}}></GlobalFilter>
                                    </Col>
                                </Row>
                                <Row className="py-2">
                                    <AgGrid
                                        getRowId={(p) => `${p.data.managedStockId}|${p.data.productId}` }
                                        ref={linkGridRef}
                                        heightPx={530}
                                        rowSelectionType="multiple"
                                        onSelectionChanged={(s) => {
                                            setRowsToLink(s as LinkConflictRowData[]);
                                        }}
                                        rowData={linkConflictRowData}
                                        columnDefs={conflictColumnDefs}
                                        searchText={searchText}
                                        loading={loading}
                                        isRowSelectable={(row: IRowNode<LinkConflictRowData>) => row.data?.conflict != null}
                                    />
                                </Row>
                                <Row>
                                    <Col xs={12} className="d-flex flex-row-reverse">
                                        <ZTooltip enabled={rowsToLink.length === 0 || selectedStocksHaveError} text={rowsToLink.length === 0 ? `Please select at least 1 stock to link` : `One or more of the selected stocks have an error and cannot be linked`}>
                                            <ZButton
                                                buttonText="Link Selected"
                                                variant="primary"
                                                disabled={rowsToLink.length === 0 || selectedStocksHaveError}
                                                className="cta-button"
                                                onClick={() => {
                                                    setRowsToLink(linkGridRef.current?.getSelectedRows() as LinkConflictRowData[] ?? []);
                                                    setShowLinkConfirmPrompt(true);
                                                }}
                                            />
                                        </ZTooltip>
                                    </Col>
                                </Row>
                            </Tab>
                            <Tab eventKey={"delete"} title={<span className="d-inline-flex align-items-center">Delete <ZCounter className="ms-1" counter={deleteConflictRowData.length} /></span>} className="pt-2">
                                <Row className="py-2">
                                    <AgGrid
                                        getRowId={(p) => `${p.data.managedStockId}|${p.data.productId}` }
                                        ref={deleteGridRef}
                                        heightPx={530}
                                        rowSelectionType="multiple"
                                        onSelectionChanged={(s) => {
                                            setRowsToDelete(s as DeleteConflictRowData[]);
                                        }}
                                        rowData={deleteConflictRowData}
                                        columnDefs={defaultDeleteConflictColumnDefs}
                                        loading={loading}
                                    />
                                </Row>
                                <Row>
                                    <Col xs={12} className="d-flex flex-row-reverse">
                                        <ZTooltip enabled={rowsToDelete.length === 0 || selectedStocksHaveError} text={rowsToDelete.length === 0 ? `Please select at least 1 stock to fix` : `One or more of the selected stocks have an error and cannot be fixed`}>
                                            <ZButton
                                                buttonText="Fix Selected"
                                                variant="primary"
                                                disabled={rowsToDelete.length === 0 || selectedStocksHaveError}
                                                className="cta-button"
                                                onClick={() => {
                                                    setRowsToDelete(deleteGridRef.current?.getSelectedRows() as DeleteConflictRowData[] ?? []);
                                                    setShowDeleteConfirmPrompt(true);
                                                }}
                                            />
                                        </ZTooltip>
                                    </Col>
                                </Row>
                            </Tab>
                        </Tabs>
                            
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}